/**
 * EventWatcher
 * 
 * Adiciona listener nas atividades do usuário para monitorar se o 
 * usuário está atuando no sistema. 
 * Todo evento do sistema é verificado (com delay de 500 ms) se o usuário 
 * está executando atividades no sistema dentro de um determinado período 
 * {activityTime}. Se este período verificar é executado a ação que for passada
 * {onActivity} definido um novo tempo de comparação no localStorage.
 * 
 * Como utilizar:
 * 1. Inicia a classe de watcher:
 *  this.eventWatcher = new EventWatcher({
        context: 'CHANNEL',
        activityTime,
        onActivity: this.handleActivity
    })
    this.eventWatcher.startWatcher()
 * 2. No destroy do componente lembrar de destruir s listeners
 * if (this.eventWatcher) this.eventWatcher.stopWatcher()   
 */

interface Params {
  activityTime: number // Time to check if user is active in this period
  onActivity: () => void // Function to call when user is active in this interval of time
  context: string // Watcher context to set in localStorage
}

export default class EventWatcher {
  private localStorageKey = 'EHR_last_activity_time'
  private events = ['click', 'keydown']
  private timeoutUpdate: number | undefined
  private activityTime: number
  private onActivity: () => void
  private eventHandler: () => void

  constructor({ context = 'EHR', activityTime = 300000, onActivity }: Params) {
    this.localStorageKey = context + '_last_activity_time'
    this.activityTime = activityTime
    this.onActivity = onActivity
    this.eventHandler = () => undefined
  }

  startWatcher() {
    this.eventHandler = this.updateLastActivityTime.bind(this)
    this.listenUserEvents()
  }

  stopWatcher = () => {
    if (this.timeoutUpdate) clearTimeout(this.timeoutUpdate)
    this.events.forEach(type => {
      window.removeEventListener(type, this.eventHandler)
    })
    localStorage.removeItem(this.localStorageKey)
  }

  private setUpdatedTime() {
    localStorage.setItem(this.localStorageKey, String(Date.now()))
  }

  private updateLastActivityTime() {
    if (this.timeoutUpdate) clearTimeout(this.timeoutUpdate)
    this.timeoutUpdate = setTimeout(() => {
      const expirationTime =
        Number(localStorage.getItem(this.localStorageKey) || 0) +
        this.activityTime
      if (expirationTime < Date.now()) {
        this.onActivity()
        this.setUpdatedTime()
      }
    }, 500) // Delay 0,5 seconds to check
  }

  private listenUserEvents() {
    this.events.forEach(type => {
      document.addEventListener(type, this.eventHandler)
    })
  }
}
